import { hydrateBoardSeat, type DeskBoardSeat } from "./companySync";

/** Machine-local sync client prefs. Not part of the snapshot (token stays on this PC). */
export const SYNC_SETTINGS_KEY = "ns.dispatch.sync";

export const DEFAULT_SYNC_PORT = 8787;

/**
 * Public Nanode the desk, the phone, and a second PC can all open.
 * A saved name or http://127.0.0.1 is a different machine. This is not a secret.
 */
export const NANODE_PUBLIC_SYNC_URL = "http://172.105.102.204:8787";

/** Phone and Twilio callbacks. Not the desk sync listener. */
export const VOICE_PROXY_HOST = "voice.northstardispatch.ca";

export type SyncClientSettings = {
  /** Base URL only, no trailing path. Example: http://127.0.0.1:8787 */
  baseUrl: string;
  /** Bearer token that matches the stub’s SYNC_TOKEN. Masked in Settings. */
  token: string;
  /**
   * Ops desk: mint auto-inbox trickle RFQs and silently Push the board.
   * Join / Pull sets false. Confirmed overwrite-Push sets true.
   * Machine-local — not on the snapshot. Missing on older localStorage — treat as true.
   */
  inboxPublisher?: boolean;
  /**
   * Sticky seat. Join / take-Nanode is follower forever on this PC so a leftover
   * Dispatch login or re-checked ops-desk box cannot Push over Emerson.
   * Create / confirmed overwrite-Push is the sim publisher.
   */
  boardSeat?: "publisher" | "follower";
  /**
   * After a local start-from-scratch wipe: pause auto-Pull and auto-Push so
   * Nanode’s old board does not silently refill this desk. Manual Pull / Push
   * or an explicit “also clear Nanode” clears it. Machine-local.
   */
  holdRemoteBoard?: boolean;
  /**
   * True only after an explicit Join / take-Nanode. Same-PC Pilot login must
   * not set this — Switch user back to Dispatch stays the sim publisher.
   */
  joinedRemote?: boolean;
};

export const EMPTY_SYNC_SETTINGS: SyncClientSettings = {
  baseUrl: "",
  token: "",
  inboxPublisher: true,
  boardSeat: "publisher",
  holdRemoteBoard: false,
  joinedRemote: false,
};

export type SyncUrlKind = "empty" | "invalid" | "loopback" | "local-name" | "ip" | "hostname";

export type SyncUrlTarget = {
  raw: string;
  url: string;
  host: string;
  kind: SyncUrlKind;
  detail: string;
};

/** Turn a pasted host:port into an absolute http URL and drop a trailing slash or /health. */
export function normalizeSyncBaseUrl(raw: string): string {
  const trimmed = raw.trim().replace(/\/+$/, "");
  if (!trimmed) return "";
  const withScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) ? trimmed : `http://${trimmed}`;
  try {
    const parsed = new URL(withScheme);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return trimmed;
    const path = parsed.pathname.replace(/\/+$/, "").replace(/\/health$/i, "");
    const port = parsed.port ? `:${parsed.port}` : "";
    return `${parsed.protocol}//${parsed.hostname}${port}${path}`;
  } catch {
    return trimmed;
  }
}

function ipv4(host: string): boolean {
  const parts = host.split(".");
  return parts.length === 4 && parts.every((part) => /^\d{1,3}$/.test(part) && Number(part) <= 255);
}

/** Say which machine a saved sync URL actually names, before any request is sent. */
export function describeSyncTarget(raw: string): SyncUrlTarget {
  const trimmed = raw.trim();
  if (!trimmed) {
    return {
      raw: "",
      url: "",
      host: "",
      kind: "empty",
      detail: "No sync server URL is saved on this desk.",
    };
  }
  const url = normalizeSyncBaseUrl(trimmed);
  let host = "";
  try {
    host = new URL(url).hostname;
  } catch {
    return {
      raw: trimmed,
      url,
      host: "",
      kind: "invalid",
      detail: `The saved sync URL cannot be opened: ${trimmed}.`,
    };
  }
  if (!host || !/^https?:\/\//i.test(url)) {
    return { raw: trimmed, url, host, kind: "invalid", detail: `The saved sync URL cannot be opened: ${trimmed}.` };
  }
  const lower = host.toLowerCase();
  if (lower === "localhost" || lower === "127.0.0.1" || lower === "::1" || lower === "[::1]") {
    return {
      raw: trimmed,
      url,
      host,
      kind: "loopback",
      detail: `This URL is ${url}. That is this PC, not the Nanode. The address a browser can open is ${NANODE_PUBLIC_SYNC_URL}.`,
    };
  }
  if (lower.endsWith(".local")) {
    return {
      raw: trimmed,
      url,
      host,
      kind: "local-name",
      detail: `Host ${host} is a local name for a desk on this network, not the Nanode. The address a browser can open is ${NANODE_PUBLIC_SYNC_URL}.`,
    };
  }
  if (ipv4(host)) {
    return {
      raw: trimmed,
      url,
      host,
      kind: "ip",
      detail: `Host is the IP ${host}. The desk opens ${url}.`,
    };
  }
  return {
    raw: trimmed,
    url,
    host,
    kind: "hostname",
    detail: `Host ${host} is a name, not an IP. The desk opens ${url}. If this PC cannot resolve that name, the result is Failed to fetch. The address a browser can open is ${NANODE_PUBLIC_SYNC_URL}.`,
  };
}

export function fetchFailureDetail(url: string, error: string): string {
  const target = describeSyncTarget(url);
  const opened = target.url || url || "the saved sync URL";
  return `Failed to fetch ${opened}. ${error} The desk could not open this URL, so this is not a refused sign-in. ${target.detail} ${dnsReport(url, false)}`.replace(/\s+/g, " ").trim();
}

/** What this PC can know about the name. An IP needs no lookup. A failed fetch cannot separate a missing name from a name that points somewhere else. */
export function dnsReport(raw: string, opened: boolean | null): string {
  const target = describeSyncTarget(raw);
  if (target.kind === "ip" || target.kind === "loopback") {
    return `DNS: not needed. Host is ${target.host}. Opening ${target.url}.`;
  }
  if (target.kind === "local-name") {
    return `DNS: ${target.host} does not resolve on the public internet. Opening ${target.url}.`;
  }
  if (target.kind === "hostname") {
    if (opened === true) return `DNS: ${target.host} resolved. Opening ${target.url}.`;
    if (opened === false) {
      return `DNS: ${target.host} did not answer on this URL. The name may not resolve here, or it may point somewhere other than the sync server. Opening ${target.url}.`;
    }
    return `DNS: ${target.host} is a name, not an IP. Opening ${target.url}.`;
  }
  return target.detail;
}

/**
 * The sim PC kept an account URL of this machine while sync settings, or the
 * other desk, used the Nanode. Prefer the URL that is not loopback or a .local name.
 */
export function isVoiceProxyUrl(raw: string): boolean {
  const normalized = normalizeSyncBaseUrl(raw);
  try {
    return new URL(normalized).hostname.toLowerCase() === VOICE_PROXY_HOST;
  } catch {
    return raw.toLowerCase().includes(VOICE_PROXY_HOST);
  }
}

/** The desk sync URL. A saved voice-proxy hostname is the phone callback host, not this listener. */
export function deskSyncBaseUrl(raw: string): string {
  const normalized = normalizeSyncBaseUrl(raw);
  if (!normalized) return "";
  if (isVoiceProxyUrl(normalized)) return NANODE_PUBLIC_SYNC_URL;
  return normalized;
}

export function syncUrlForDesk(input: { syncBaseUrl?: string; sessionBaseUrl?: string }): { url: string; note: string } {
  const syncUrl = deskSyncBaseUrl(input.syncBaseUrl || "");
  const sessionUrl = deskSyncBaseUrl(input.sessionBaseUrl || "");
  if (!syncUrl) return { url: sessionUrl, note: "" };
  if (!sessionUrl || syncUrl === sessionUrl) return { url: syncUrl, note: "" };
  const syncKind = describeSyncTarget(syncUrl).kind;
  const sessionKind = describeSyncTarget(sessionUrl).kind;
  const local = (kind: SyncUrlKind) => kind === "loopback" || kind === "local-name";
  if (local(sessionKind) && !local(syncKind)) {
    return {
      url: syncUrl,
      note: `The account session still says ${sessionUrl}. This desk is using the sync setting ${syncUrl}.`,
    };
  }
  if (local(syncKind) && !local(sessionKind)) {
    return {
      url: sessionUrl,
      note: `Sync settings still say ${syncUrl}. This desk is using the account URL ${sessionUrl}.`,
    };
  }
  return {
    url: syncUrl,
    note: `Sync settings ${syncUrl} and the account session ${sessionUrl} differ. This desk is using ${syncUrl}.`,
  };
}

export function syncConfigured(settings: SyncClientSettings): boolean {
  return Boolean(normalizeSyncBaseUrl(settings.baseUrl) && settings.token.trim());
}

export type SyncLinkStatus = "unset" | "checking" | "ok" | "error";

/**
 * Health probes must not flash desk chrome. Keep the last settled
 * connected / error label until the probe result arrives.
 */
export function syncStatusWhileProbing(current: SyncLinkStatus): SyncLinkStatus {
  if (current === "ok" || current === "error") return current;
  return "checking";
}

/** Consecutive failed /health probes before Ops leaves Connected. */
export const SYNC_LINK_ERROR_STREAK = 3;
/** Stay Connected this long after the last successful health or Pull. */
export const SYNC_LINK_OK_HOLD_MS = 12_000;
/** Pilot: a Pull / revision that answered recently means Nanode is reachable. */
export const SYNC_LINK_PULL_OK_MS = 15_000;

export function syncClientSettingsEqual(a: SyncClientSettings, b: SyncClientSettings): boolean {
  return (
    a.baseUrl === b.baseUrl &&
    a.token === b.token &&
    (a.inboxPublisher !== false) === (b.inboxPublisher !== false) &&
    hydrateBoardSeat(a.boardSeat, a.inboxPublisher) === hydrateBoardSeat(b.boardSeat, b.inboxPublisher) &&
    (a.holdRemoteBoard === true) === (b.holdRemoteBoard === true) &&
    (a.joinedRemote === true) === (b.joinedRemote === true)
  );
}

export type SettleSyncLinkInput = {
  current: SyncLinkStatus;
  configured: boolean;
  probeOk: boolean;
  failStreak: number;
  lastOkAtMs: number | null;
  nowMs: number;
  /** Last time Pull / revision / a 404-from-Nanode proved the host is up. */
  pullOkAtMs?: number | null;
  role?: string | null;
};

export type SettleSyncLinkResult = {
  status: SyncLinkStatus;
  failStreak: number;
  lastOkAtMs: number | null;
  announceError: boolean;
};

/**
 * Ops Connected / Error with hysteresis. One failed /health must not flap
 * the header. Pilot desks treat a fresh Pull / revision as healthy even when
 * the unauthenticated /health probe blips (Push is not required).
 */
export function settleSyncLinkStatus(input: SettleSyncLinkInput): SettleSyncLinkResult {
  if (!input.configured) {
    return { status: "unset", failStreak: 0, lastOkAtMs: input.lastOkAtMs, announceError: false };
  }
  const pullFresh =
    input.pullOkAtMs != null && input.nowMs - input.pullOkAtMs <= SYNC_LINK_PULL_OK_MS;
  const reachable = input.probeOk || pullFresh;
  if (reachable) {
    return { status: "ok", failStreak: 0, lastOkAtMs: input.nowMs, announceError: false };
  }

  const streak = input.failStreak + 1;
  const heldOk =
    input.current === "ok" &&
    ((input.lastOkAtMs != null && input.nowMs - input.lastOkAtMs < SYNC_LINK_OK_HOLD_MS) ||
      streak < SYNC_LINK_ERROR_STREAK);
  if (heldOk) {
    return {
      status: "ok",
      failStreak: streak,
      lastOkAtMs: input.lastOkAtMs,
      announceError: false,
    };
  }
  if (input.current === "checking" && streak < SYNC_LINK_ERROR_STREAK) {
    return {
      status: "checking",
      failStreak: streak,
      lastOkAtMs: input.lastOkAtMs,
      announceError: false,
    };
  }
  return {
    status: "error",
    failStreak: streak,
    lastOkAtMs: input.lastOkAtMs,
    announceError: input.current !== "error",
  };
}

/** Banner / empty-copy: true while last success is fresh, even mid-probe. */
export function syncReachableFlag(input: {
  status: SyncLinkStatus;
  lastOkAtMs: number | null;
  nowMs: number;
}): boolean | null {
  if (input.status === "unset") return null;
  if (input.status === "ok") return true;
  const fresh = input.lastOkAtMs != null && input.nowMs - input.lastOkAtMs < SYNC_LINK_OK_HOLD_MS;
  if (fresh) return true;
  if (input.status === "error") return false;
  return null;
}

export function loadSyncSettings(storage?: Pick<Storage, "getItem"> | null): SyncClientSettings {
  try {
    const raw = storage?.getItem(SYNC_SETTINGS_KEY);
    if (!raw) return { ...EMPTY_SYNC_SETTINGS };
    const parsed = JSON.parse(raw) as Partial<SyncClientSettings>;
    const inboxPublisher = parsed.inboxPublisher !== false;
    return {
      baseUrl: typeof parsed.baseUrl === "string" ? deskSyncBaseUrl(parsed.baseUrl) : "",
      token: typeof parsed.token === "string" ? parsed.token : "",
      inboxPublisher,
      boardSeat: hydrateBoardSeat(parsed.boardSeat, inboxPublisher),
      holdRemoteBoard: parsed.holdRemoteBoard === true,
      joinedRemote: parsed.joinedRemote === true,
    };
  } catch {
    return { ...EMPTY_SYNC_SETTINGS };
  }
}

export function saveSyncSettings(
  settings: SyncClientSettings,
  storage?: Pick<Storage, "setItem"> | null,
): SyncClientSettings {
  const inboxPublisher = settings.inboxPublisher !== false;
  const boardSeat: DeskBoardSeat = hydrateBoardSeat(settings.boardSeat, inboxPublisher);
  const next = {
    baseUrl: deskSyncBaseUrl(settings.baseUrl),
    token: settings.token.trim(),
    inboxPublisher: boardSeat === "follower" ? false : inboxPublisher,
    boardSeat,
    holdRemoteBoard: settings.holdRemoteBoard === true,
    joinedRemote: settings.joinedRemote === true,
  };
  storage?.setItem(SYNC_SETTINGS_KEY, JSON.stringify(next));
  return next;
}
